// Sleek smart date input — live-parity port of sleek-app-v2 src/public/js/sleek-date.js.
// Accountants type digits only: "1703" becomes "17/03/2026" with the year filled from the
// financial period, then focus jumps to the field named by data-next. Includes the calendar
// popup (button is tabindex -1 so tabbing skips it), period validation (red border +
// tooltip) and select-all on focus. Enter is swallowed like live — typing drives the flow.
//
// Usage:
//   <sleek-date id="cbDate" class="cb-w-date" data-next="#cbGlSearch"
//               .value=${iso} @change=${e => { iso = e.target.value; }}></sleek-date>
// .value is ALWAYS ISO (yyyy-mm-dd) or ''. data-free="true" skips period validation.
// A .value written while the user types never rewrites the entry: the box's own date handed back is
// ignored, and another date set before the entry reads as a date is shown once they leave the box.
// data-past="true" also accepts EARLIER financial years (never a later one) — the journal date
// for Owners/Admins; the dd/mm year fill still uses the current financial year.
// Call configureSleekDate(reference) once /bank-reference loads (date_format, periods,
// fy_start_day/fy_start_month); before that the component falls back to dd/mm/yyyy + a
// locally calculated financial year, exactly like live's _calcPeriodsLocally.

const pad = n => (n < 10 ? '0' + n : String(n));
const currentYearGuess = () => new Date().getFullYear();

export function normalizeFormat(raw){
  const f = String(raw || '').toLowerCase().trim();
  if(f === 'yyyy-mm-dd') return f;
  if(f === 'mm/dd/yyyy' || f === 'mm-dd-yyyy') return f;
  if(f === 'dd/mm/yyyy' || f === 'dd-mm-yyyy' || f === 'dd.mm.yyyy') return f;   // dd.mm.yyyy: Finland
  return 'dd/mm/yyyy';        // text-month formats can't be typed digits-only — live default
}

// cfg = { fmt, sep, monthToYear, periodStart, periodEnd } — build it from the company
// reference; with no period rows, derive the FY around today from the year-end month.
export function buildConfig(reference){
  const ref = reference || {};
  const fmt = normalizeFormat(ref.date_format);
  const sep = fmt.indexOf('-') !== -1 ? '-' : (fmt.indexOf('.') !== -1 ? '.' : '/');
  const periods = ref.periods || [];
  const monthToYear = {};
  let periodStart = '', periodEnd = '';
  if(periods.length){
    periodStart = periods[0].start;
    periodEnd = periods[periods.length - 1].end;
    periods.forEach(p => {
      monthToYear[parseInt(p.start.slice(5, 7), 10)] = parseInt(p.start.slice(0, 4), 10);
    });
  } else {
    // derive the FY around today from the company's FY START (day+month), mirroring
    // the server's src/app/fy.py (day clamped to short months)
    const sm = Number(ref.fy_start_month) || 3, sd = Number(ref.fy_start_day) || 1;
    const clamp = (yy, mm, dd) => new Date(yy, mm - 1, Math.min(dd, new Date(yy, mm, 0).getDate()));
    const today = new Date();
    let start = clamp(today.getFullYear(), sm, sd);
    if(today < start) start = clamp(today.getFullYear() - 1, sm, sd);
    const end = new Date(clamp(start.getFullYear() + 1, sm, sd).getTime() - 86400000);
    periodStart = start.getFullYear() + '-' + pad(start.getMonth() + 1) + '-' + pad(start.getDate());
    periodEnd = end.getFullYear() + '-' + pad(end.getMonth() + 1) + '-' + pad(end.getDate());
    let m = start.getMonth() + 1, y = start.getFullYear();
    for(let i = 0; i < 12; i++){
      monthToYear[m] = y;
      m += 1; if(m > 12){ m = 1; y += 1; }
    }
  }
  return { fmt, sep, monthToYear, periodStart, periodEnd };
}

export function yearForMonth(month, cfg){
  return cfg.monthToYear[month] || currentYearGuess();
}

export function isValidDate(d, m, y){
  if(m < 1 || m > 12 || d < 1 || d > 31) return false;
  return d <= new Date(y, m, 0).getDate();
}

export function isInPeriod(iso, cfg){
  if(!cfg.periodStart || !cfg.periodEnd) return true;
  return iso >= cfg.periodStart && iso <= cfg.periodEnd;
}

// The dates an input accepts. 'free' → any date; 'past' → the financial period OR any earlier date
// (earlier financial years, never a later one); 'period' (the default) → the financial period only.
export function inAllowedRange(iso, cfg, mode){
  if(mode === 'free') return true;
  if(mode === 'past') return !cfg.periodEnd || iso <= cfg.periodEnd;
  return isInPeriod(iso, cfg);
}

export function autoFormat(digits, cfg){
  if(cfg.fmt === 'yyyy-mm-dd'){
    const yearStr = String(currentYearGuess());
    if(digits.length <= 4) return yearStr;
    const mm = digits.substring(4, 6), dd = digits.substring(6, 8);
    let r = yearStr + '-' + mm;
    if(dd) r += '-' + dd;
    return r;
  }
  const p1 = digits.substring(0, 2), p2 = digits.substring(2, 4);
  if(digits.length <= 2) return p1;
  if(digits.length <= 4) return p1 + cfg.sep + p2;
  return p1 + cfg.sep + p2 + cfg.sep + digits.substring(4, 8);
}

export function displayToIso(display, cfg){
  const parts = String(display || '').split(/[\/\-.]/);
  if(parts.length < 2) return '';
  let day, month, year;
  if(cfg.fmt === 'yyyy-mm-dd'){
    year = parseInt(parts[0], 10); month = parseInt(parts[1], 10);
    day = parts[2] ? parseInt(parts[2], 10) : 0;
  } else if(cfg.fmt.slice(0, 2) === 'mm'){
    month = parseInt(parts[0], 10); day = parseInt(parts[1], 10);
    year = parts[2] ? parseInt(parts[2], 10) : yearForMonth(month, cfg);
  } else {
    day = parseInt(parts[0], 10); month = parseInt(parts[1], 10);
    year = parts[2] ? parseInt(parts[2], 10) : yearForMonth(month, cfg);
  }
  if(!year) year = currentYearGuess();
  if(!isValidDate(day, month, year)) return '';
  return year + '-' + pad(month) + '-' + pad(day);
}

export function isoToDisplay(iso, cfg){
  const parts = String(iso).split('-');
  if(parts.length !== 3) return String(iso);
  const [y, m, d] = parts;
  if(cfg.fmt === 'yyyy-mm-dd') return y + '-' + m + '-' + d;
  if(cfg.fmt.slice(0, 2) === 'mm') return m + cfg.sep + d + cfg.sep + y;
  return d + cfg.sep + m + cfg.sep + y;
}

// Blur completion: "1703" / "17/03" gets its period year, full 8 digits get formatted.
export function tryComplete(val, isFree, cfg){
  const cleaned = String(val || '').replace(/[^\d]/g, '');
  if(cfg.fmt === 'yyyy-mm-dd'){
    if(cleaned.length === 8) return autoFormat(cleaned, cfg);
    if(cleaned.length >= 6){
      let full = String(currentYearGuess()) + cleaned.substring(4);
      while(full.replace(/[^\d]/g, '').length < 8) full += '0';
      return autoFormat(full.replace(/[^\d]/g, '').substring(0, 8), cfg);
    }
    return null;
  }
  if(cleaned.length === 4){
    const month = cfg.fmt.slice(0, 2) === 'mm'
      ? parseInt(cleaned.substring(0, 2), 10) : parseInt(cleaned.substring(2, 4), 10);
    const yr = isFree ? currentYearGuess() : yearForMonth(month, cfg);
    return autoFormat(cleaned + String(yr), cfg);
  }
  if(cleaned.length === 8) return autoFormat(cleaned, cfg);
  return null;
}

// ── the <sleek-date> element (browser only — pure core above is what vitest runs) ──
let _cfg = buildConfig(null);
const _instances = typeof Set !== 'undefined' ? new Set() : null;

export function configureSleekDate(reference){
  _cfg = buildConfig(reference);
  if(_instances) _instances.forEach(el => el._applyConfig());
}

const _ALLOW_KEYS = ['Backspace', 'Delete', 'Tab', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
const _CAL_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>';
const _DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const _MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
                 'September', 'October', 'November', 'December'];

const Base = typeof HTMLElement !== 'undefined' ? HTMLElement : class {};

class SleekDateElement extends Base {
  constructor(){
    super();
    this._iso = '';
    this._input = null;
    this._typing = false;      // the user has typed and not yet left the box
    this._held = undefined;    // a date a screen put in while that typing held no date yet (see set value)
    this._cal = null;
    this._calYear = 0; this._calMonth = 0;
  }

  get value(){ return this._iso; }
  set value(iso){
    iso = String(iso || '');
    const next = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '';
    if(this._input && document.activeElement === this._input){
      // A screen that binds .value to a signal its own @change writes (Governance, Reports, …) hands the box
      // its own date straight back while the user types. Rewriting the text then lost what was being typed:
      // the first digit over a filled box, or "01/1" (already 1 January) before the "0" of October could land
      // (staging 2026-10-09). That echo carries the date the box already holds — leave the typing alone.
      if(next === this._iso){ this._held = undefined; return; }
      // A screen can also answer a key with a DIFFERENT date while the typing holds no date yet (cleared, or
      // too short to read): Reports reload and fill the empty box with the server's period, Batches falls back
      // to today. Written now it replaced the half-typed entry — "1509" typed at a person's pace into the VAT
      // report's From box became 01/03/2026 (2026-10-09). Hold it until the user leaves the box: a date they
      // finish replaces it, and leaving without one shows it, as before.
      if(this._typing && !this._iso){ this._held = next; return; }
    }
    this._held = undefined;
    this._iso = next;
    if(this._input){
      this._input.value = this._iso ? isoToDisplay(this._iso, _cfg) : '';
      this._clearError();
    }
  }

  focus(){ if(this._input) this._input.focus(); }

  connectedCallback(){
    _instances.add(this);
    if(this._built) return;
    this._built = true;
    // template-built DOM upgrades late: a .value set before upgrade lands as an own
    // property that shadows the class accessor — re-deliver it through the setter
    if(Object.prototype.hasOwnProperty.call(this, 'value')){
      const shadowed = this.value;
      delete this.value;
      this._iso = /^\d{4}-\d{2}-\d{2}$/.test(String(shadowed || '')) ? String(shadowed) : '';
    }
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'form-control cb-in sleek-date-in';
    input.autocomplete = 'off';
    input.inputMode = 'numeric';
    this._input = input;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sleek-date-cal-btn';
    btn.tabIndex = -1;                                   // live: tabbing skips the calendar
    btn.title = 'Calendar';
    btn.innerHTML = _CAL_SVG;
    this.appendChild(input);
    this.appendChild(btn);
    this._applyConfig();
    input.addEventListener('keydown', e => this._onKeyDown(e));
    input.addEventListener('input', () => this._onInput());
    input.addEventListener('focus', () => this._onFocus());
    input.addEventListener('blur', () => this._onBlur());
    // the inner input's native change must not leak — the host owns the 'change' event
    input.addEventListener('change', e => e.stopPropagation());
    btn.addEventListener('click', e => {
      e.preventDefault(); e.stopPropagation();
      this._cal ? this._closeCalendar() : this._openCalendar();
    });
  }

  disconnectedCallback(){
    _instances.delete(this);
    this._closeCalendar();
  }

  _applyConfig(){
    if(!this._input) return;
    const free = this.dataset.free === 'true';
    if(_cfg.fmt === 'yyyy-mm-dd') this._input.placeholder = currentYearGuess() + '-mm-dd';
    else {
      const short = _cfg.fmt.slice(0, 2) === 'mm' ? 'mm' + _cfg.sep + 'dd' : 'dd' + _cfg.sep + 'mm';
      // Always show the FULL format incl. year (owner 2026-09-19: "shows dd/mm — should be
      // dd/mm/YYYY"). Typing just day+month still auto-fills the period year on input; the
      // placeholder just no longer hides the year and reads as complete.
      this._input.placeholder = short + _cfg.sep + 'yyyy';   // e.g. dd/mm/yyyy
    }
    this._input.maxLength = _cfg.fmt.length;
    if(this._iso) this._input.value = isoToDisplay(this._iso, _cfg);
  }

  _commit(iso){
    iso = iso || '';
    if(iso === this._iso) return;
    this._iso = iso;
    this.dispatchEvent(new CustomEvent('change', { bubbles: true }));
  }

  // which dates this input accepts — see inAllowedRange (data-free wins over data-past)
  _mode(){ return this.dataset.free === 'true' ? 'free' : (this.dataset.past === 'true' ? 'past' : 'period'); }
  _clearError(){ this._input.classList.remove('sleek-date-error'); this._input.title = ''; }
  _showError(){
    this._input.classList.add('sleek-date-error');
    this._input.title = this._mode() === 'past'
      ? 'Outside period: after ' + isoToDisplay(_cfg.periodEnd, _cfg)
      : 'Outside period: ' + isoToDisplay(_cfg.periodStart, _cfg) + ' – ' + isoToDisplay(_cfg.periodEnd, _cfg);
  }

  _onKeyDown(e){
    const key = e.key;
    if(_ALLOW_KEYS.indexOf(key) !== -1) return;
    if((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].indexOf(key) !== -1) return;
    if(!/^\d$/.test(key) && key !== _cfg.sep) e.preventDefault();   // Enter included, like live
  }

  _onInput(){
    const el = this._input;
    this._typing = true;
    let raw = el.value.replace(/[^\d]/g, '');
    if(_cfg.fmt === 'yyyy-mm-dd'){
      const yearStr = String(currentYearGuess());
      raw = raw.length < 4 ? yearStr : yearStr + raw.substring(4);
    }
    if(_cfg.fmt !== 'yyyy-mm-dd' && raw.length === 4){
      const month = _cfg.fmt.slice(0, 2) === 'mm'
        ? parseInt(raw.substring(0, 2), 10) : parseInt(raw.substring(2, 4), 10);
      const yr = this.dataset.free === 'true' ? currentYearGuess() : yearForMonth(month, _cfg);
      raw = raw + String(yr);
    }
    const formatted = autoFormat(raw, _cfg);
    el.value = formatted;
    el.setSelectionRange(formatted.length, formatted.length);
    this._clearError();
    const iso = displayToIso(formatted, _cfg);
    this._commit(iso);
    if(iso && formatted.length === _cfg.fmt.length){
      if(!inAllowedRange(iso, _cfg, this._mode())) this._showError();
      else this._autoTab();
    }
  }

  _onFocus(){
    const el = this._input;
    setTimeout(() => {
      if(_cfg.fmt === 'yyyy-mm-dd'){
        if(!el.value || el.value.length < 5){
          el.value = currentYearGuess() + '-';
          el.setSelectionRange(5, el.value.length);
        } else el.select();
      } else el.select();               // first keystroke starts a fresh date, like live
    }, 0);
  }

  _onBlur(){
    this._typing = false;
    const el = this._input, val = el.value.trim();
    if(!val){ this._commit(''); this._clearError(); this._showHeld(); return; }
    const completed = tryComplete(val, this.dataset.free === 'true', _cfg);
    if(completed){
      el.value = completed;
      const iso = displayToIso(completed, _cfg);
      this._commit(iso);
      if(iso && !inAllowedRange(iso, _cfg, this._mode())) this._showError();
      else this._clearError();
    }
    this._showHeld();
  }

  // the date a screen put in while the typing held none (set value) — shown once the user has left the box,
  // unless what they typed became a date
  _showHeld(){
    const held = this._held;
    this._held = undefined;
    if(held !== undefined && !this._iso) this.value = held;
  }

  _autoTab(){
    // live walks the row when data-next is absent; every v-next call site names its
    // target explicitly, so only data-next is ported
    const nextSel = this.dataset.next;
    if(!nextSel) return;
    const target = document.querySelector(nextSel);
    if(target) target.focus();
  }

  // ── calendar popup (live parity: month grid, ‹ ›, period-locked days) ──
  _openCalendar(){
    const iso = this._iso;
    const now = new Date();
    if(iso){ this._calYear = parseInt(iso.substring(0, 4), 10); this._calMonth = parseInt(iso.substring(5, 7), 10) - 1; }
    else { this._calYear = now.getFullYear(); this._calMonth = now.getMonth(); }
    this._renderCalendar();
  }

  _closeCalendar(){
    if(this._cal && this._cal.parentNode) this._cal.parentNode.removeChild(this._cal);
    this._cal = null;
  }

  _renderCalendar(){
    this._closeCalendar();
    const cal = document.createElement('div');
    cal.className = 'sleek-cal';
    let out = '<div class="sleek-cal-hdr">' +
      '<button type="button" class="sleek-cal-nav" data-dir="-1">&lsaquo;</button>' +
      '<span class="sleek-cal-title">' + _MONTHS[this._calMonth] + ' ' + this._calYear + '</span>' +
      '<button type="button" class="sleek-cal-nav" data-dir="1">&rsaquo;</button></div>' +
      '<div class="sleek-cal-grid">';
    for(let d = 0; d < 7; d++) out += '<div class="sleek-cal-dh">' + _DAYS[d] + '</div>';
    const first = new Date(this._calYear, this._calMonth, 1).getDay();
    const daysInMonth = new Date(this._calYear, this._calMonth + 1, 0).getDate();
    const now = new Date();
    const todayIso = now.getFullYear() + '-' + pad(now.getMonth() + 1) + '-' + pad(now.getDate());
    const mode = this._mode();
    const minDate = this.dataset.min || '', maxDate = this.dataset.max || '';
    for(let b = 0; b < first; b++) out += '<div class="sleek-cal-day sleek-cal-blank"></div>';
    for(let i = 1; i <= daysInMonth; i++){
      const iso = this._calYear + '-' + pad(this._calMonth + 1) + '-' + pad(i);
      let cls = 'sleek-cal-day';
      if(iso === todayIso) cls += ' sleek-cal-today';
      if(iso === this._iso) cls += ' sleek-cal-sel';
      if(!inAllowedRange(iso, _cfg, mode) || (maxDate && iso > maxDate) || (minDate && iso < minDate))
        cls += ' sleek-cal-out';
      out += '<div class="' + cls + '" data-iso="' + iso + '">' + i + '</div>';
    }
    cal.innerHTML = out + '</div>';
    this.appendChild(cal);
    this._cal = cal;
    // Phones only (owner 2026-10-19: "everything to fit inside the mobile window"). The calendar hangs 240px
    // wide from its box's left edge (aboo.css .sleek-cal), so a date box in the right half of a phone opened
    // it past the screen (sales memos' "to" date: 31-66px out at 360-430px). Slide it left just enough to keep
    // 8px from the edge, never past 8px on the left. 768px and up is left exactly as it was: the same media
    // query as the PHONE FIT block in ux.css.
    if(window.matchMedia && window.matchMedia('(max-width:767px)').matches){
      const r = cal.getBoundingClientRect(), over = r.right - (window.innerWidth - 8);
      if(over > 0) cal.style.left = -Math.max(0, Math.min(over, r.left - 8)) + 'px';
    }
    cal.addEventListener('click', e => {
      e.stopPropagation();
      const nav = e.target.closest('.sleek-cal-nav');
      if(nav){
        this._calMonth += parseInt(nav.getAttribute('data-dir'), 10);
        if(this._calMonth < 0){ this._calMonth = 11; this._calYear--; }
        if(this._calMonth > 11){ this._calMonth = 0; this._calYear++; }
        this._renderCalendar();
        return;
      }
      const day = e.target.closest('.sleek-cal-day[data-iso]');
      if(day && !day.classList.contains('sleek-cal-out')){
        const picked = day.getAttribute('data-iso');
        this._input.value = isoToDisplay(picked, _cfg);
        this._clearError();
        this._closeCalendar();
        this._commit(picked);
      }
    });
  }
}

if(typeof customElements !== 'undefined' && !customElements.get('sleek-date')){
  customElements.define('sleek-date', SleekDateElement);
  document.addEventListener('click', e => {
    if(e.target.closest('.sleek-cal') || e.target.closest('.sleek-date-cal-btn')) return;
    _instances.forEach(el => el._closeCalendar());
  });
}
